import { State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { QueryObserverResult } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { SearchProductsQueryResult } from 'src/services/search_products/useSearchProductsQuery';
import { CartItem } from 'src/types/cart';
import { CatalogTreeViewItem } from 'src/types/catalog/Catalog';
import { ProductDetails } from 'src/types/catalog/ProductDetails';
import { DropDownCategories, SetState } from 'src/types/generic';
import { IFilters } from 'src/types/PostBody';
import { RecommendPageParams } from 'src/types/recommendation_items';
import { SearchCatalogOption, SearchResultProduct } from './searchProductData';

export interface SearchProductSelectionState {
  selected: boolean;
  qty: number;
}

export const initialSearchListViewDataState: State = {
  skip: 0,
  take: 100,
  sort: [{ field: 'itemNum', dir: 'asc' }],
};

export const initialSearchPictureViewDataState: State = {
  skip: 0,
  take: 25,
  sort: [{ field: 'itemNum', dir: 'asc' }],
};

export interface SearchProductFiltersContextShape {
  refineSearch: string;
  setRefineSearch: SetState<string>;
  effectiveRefineSearch: string;
  isRefineSearchActive: boolean;
  selectedView: number;
  setSelectedView: SetState<number>;
  dataState: State;
  setDataState: SetState<State>;
  catalogTreeData: CatalogTreeViewItem[];
  catalogDropdownPlaceholder: string;
  selectedCatalogFilter: SearchCatalogOption;
  setSelectedCatalogFilter: SetState<SearchCatalogOption>;
  priceRangeFilter: string;
  setPriceRangeFilter: SetState<string>;
  itemFilter: DropDownCategories;
  setItemFilter: SetState<DropDownCategories>;
  pendingFilterToastKey: string | null;
  clearPendingFilterToast: () => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  toolbarFilters: IFilters[];
  listViewTake: number;
  pictureViewTake: number;
  setListViewTake: SetState<number>;
  setPictureViewTake: SetState<number>;
  isSupplierUser: boolean;
}

export interface SearchProductQueryContextShape {
  searchTerm: string;
  trimmedSearchTerm: string;
  products: SearchResultProduct[];
  productsTotal: number;
  loadingProducts: boolean;
  isPrimarySearchPending: boolean;
  hasNoPrimarySearchResults: boolean;
  isSearchProductsError: boolean;
  fetchProducts: () => Promise<QueryObserverResult<SearchProductsQueryResult | undefined>>;
}

export interface SearchProductDetailsContextShape {
  selectedProduct: SearchResultProduct | null;
  setSelectedProduct: SetState<SearchResultProduct | null>;
  productDetails: ProductDetails | undefined;
  loadingProductDetails: boolean;
  isFetchingProductDetails: boolean;
  isProductPanelOpen: boolean;
  handleCloseProductPanel: () => void;
  handleViewProductDetails: (e: MouseEvent, product: SearchResultProduct) => Promise<void>;
  fetchProductDetails: () => Promise<QueryObserverResult<ProductDetails | undefined>>;
  getSelectedProductCatalogType: () => number | undefined;
  isSwapItemModalOpen: boolean;
  setIsSwapItemModalOpen: SetState<boolean>;
  swapItemData: RecommendPageParams | null;
  setSwapItemData: SetState<RecommendPageParams | null>;
}

export interface SearchProductSelectionsContextShape {
  productSelections: Record<string, SearchProductSelectionState>;
  setProductSelections: SetState<Record<string, SearchProductSelectionState>>;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onItemChange: (event: GridItemChangeEvent) => void;
  getProductSelection: (productKey: string) => SearchProductSelectionState;
  updateProductSelection: (
    productKey: string,
    updates: Partial<SearchProductSelectionState>
  ) => void;
  hasSelectedItems: boolean;
  clearSelections: () => void;
  folderOptions: Array<{ id: number; name: string }>;
  handleAddToFavorites: (folderId: number) => Promise<void>;
  isAddingToFavorites: boolean;
  handleSelectedItemsAddToCart: () => Promise<void>;
  handleSingleItemAddToCart: (item: CartItem) => Promise<void>;
  isUpdatingCart: boolean;
  handleConfirm: (callback: () => void | Promise<void>) => Promise<void>;
  handleRefineSearchChange: (value: string) => Promise<void>;
  handleViewChange: (viewIndex: number) => Promise<void>;
  onDataStateChange: (event: GridDataStateChangeEvent | State) => Promise<void>;
}
